import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TimeoutDialogComponent } from '../../timeout-dialog/timeout-dialog.component';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SessionStorageService } from './session-storage.service';
import { OAuthService } from './authorization/oauth.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {

  public dialogConfig: MatDialogConfig;

  private pingCushion = 5000;
  private dialogCushion = 60000;
  private cushionedDialogTimeoutLength = 600000 - this.pingCushion - this.dialogCushion;

  private hasAuthToken: boolean;

  private mousedownEventSubscription: Subscription;
  private keypressEventSubscription: Subscription;

  private userActivityIntervalPeriod = 60;
  private isUserActive = false;

  private dialogTimerSessionStorageKey = 'dialogTimer';
  private userActivityIntervalSessionStorageKey = 'userActivity';

  constructor(private dialog: MatDialog,
              private sessionStorageService: SessionStorageService,
              private oauthService: OAuthService,
              private router: Router) {
    this.dialogConfig = new MatDialogConfig();
    this.dialogConfig.disableClose = true;

    this.sessionStorageService.authTokenChange.subscribe((authToken: string) => {
      this.hasAuthToken = !!authToken;
      this.checkHasAuthToken();
    });
  }

  private checkHasAuthToken() {
    if (this.hasAuthToken) {
      this.startTimers();
      this.detectUserActivity();
    } else {
      this.clearAllTimers();
      this.unsubscribeFromEvents();
    }
  }

  private startTimers() {
    const dialogTimer = setTimeout(() => this.openDialog(), this.cushionedDialogTimeoutLength);
    sessionStorage.setItem(this.dialogTimerSessionStorageKey, dialogTimer.toString());
    this.startUserActivityInterval();
  }

  private startUserActivityInterval() {
    this.isUserActive = false;
    const userActivityInterval = setInterval(() => {
      this.userActivityIntervalPeriod--;
      if (this.userActivityIntervalPeriod <= 0) {
        if (this.isUserActive) {
          this.resetTimers();
        }
        this.userActivityIntervalPeriod = 60;
      }
    }, 1000);
    sessionStorage.setItem(this.userActivityIntervalSessionStorageKey, userActivityInterval.toString());
  }

  private detectUserActivity() {
    this.mousedownEventSubscription = fromEvent(window, 'mousedown')
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.isUserActive = true;
      });
    this.keypressEventSubscription = fromEvent(window, 'keypress')
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.isUserActive = true;
      });
  }

  public resetTimers() {
    console.log('Resetting Timers');
    this.clearAllTimers();
    this.startTimers();
  }

  public stopTimers() {
    this.clearAllTimers();
  }

  private clearAllTimers() {

    const currentDialogTimer = sessionStorage.getItem(this.dialogTimerSessionStorageKey);
    const currentInterval = sessionStorage.getItem(this.userActivityIntervalSessionStorageKey);

    if (currentDialogTimer) {
      clearTimeout(parseInt(currentDialogTimer, 10));
    }
    if (currentInterval) {
      clearInterval(parseInt(currentInterval, 10));
    }
  }

  private unsubscribeFromEvents() {
    if (this.mousedownEventSubscription) {
      this.mousedownEventSubscription.unsubscribe();
    }
    if (this.keypressEventSubscription) {
      this.keypressEventSubscription.unsubscribe();
    }
  }

  private openDialog() {
    const dialogRef = this.dialog.open(TimeoutDialogComponent, this.dialogConfig);

    dialogRef.afterClosed()
      .subscribe(result => {
        if ( result ) {
          this.resetTimers();
          this.oauthService.authorizeWithRefreshToken(this.router.url);
        } else {
          this.oauthService.signOut();
        }
      });
  }

}
